/**
 * Shared LanePredictor helpers.
 *
 * Extracted verbatim from src/pages/AtlasFreightLabPage.tsx so that both
 * Freight Lab and the shipper-side LanePredictorWidget call the SAME
 * pure logic and the SAME `atlas-lane-predictor` edge function.
 *
 * NOTE: These function bodies are byte-identical to their originals in
 * AtlasFreightLabPage.tsx. Do not modify without updating both callers.
 */

/**
 * Parse freight-friendly time strings into "HH:MM" (24h).
 * Accepts: 10am, 10:00am, 12pm, 1200, 1200 hrs, 22:30, 2:30pm, etc.
 * Returns null if unparseable.
 */
export function parseFreightTime(raw: string): string | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return null;

  // HH:MM 24h — "14:30", "8:00"
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    }
  }

  // With AM/PM — "10am", "10:00am", "2:30pm", "12pm"
  m = s.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/);
  if (m) {
    let h = Number(m[1]);
    const min = Number(m[2] || "0");
    const ampm = m[3];
    if (h < 1 || h > 12 || min < 0 || min > 59) return null;
    if (ampm === "am" && h === 12) h = 0;
    else if (ampm === "pm" && h !== 12) h += 12;
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  }

  // Military — "1200", "0800", "1200hrs"
  m = s.match(/^(\d{4})(?:hrs?)?$/);
  if (m) {
    const h = Number(m[1].slice(0, 2));
    const min = Number(m[1].slice(2));
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    }
  }

  return null;
}

/**
 * Extract the most recent "City, ST" from tracking log text.
 */
export function extractLastLocation(logs: string): string | null {
  if (!logs?.trim()) return null;
  const lines = logs.split(/\n/).filter(l => l.trim());
  const cityStateRe = /([A-Za-z][A-Za-z .'-]+,\s*[A-Z]{2})\b/;
  for (const line of lines) {
    const m = line.match(cityStateRe);
    if (m) return m[1].trim();
  }
  return null;
}

/** Combine a Date + "HH:MM" into ISO datetime string */
export function combineDateAndTime(date: Date, timeHHMM: string): string {
  const [h, m] = timeHHMM.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

/** Format a local Date to a human-readable string */
export function formatLocalDateTime(date: Date, timeHHMM: string): string {
  const [h, m] = timeHHMM.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

/** Time-of-day + season heuristic for route risk conditions. */
export function generateRouteRiskEstimate(orig: string, dest: string) {
  const now = new Date();
  const hour = now.getHours();
  const month = now.getMonth();

  const isRush = (hour >= 6 && hour <= 9) || (hour >= 16 && hour <= 19);
  const trafficSeverity = isRush ? "moderate" : "low";
  const trafficNotes = isRush
    ? `Current time ${hour}:00 falls within rush hour window for ${dest}.`
    : "No significant traffic expected at current time.";

  const isWinterMonth = month <= 1 || month === 11;
  const weatherSeverity = isWinterMonth ? "moderate" : "low";
  const weatherNotes = isWinterMonth
    ? "Winter season increases probability of adverse weather on route."
    : "Seasonal weather risk is low for this time of year.";

  const constructionSeverity = "low";
  const constructionNotes = "No known major construction zones on route.";

  let routeDelayEstimate = 0;
  if (trafficSeverity === "moderate") routeDelayEstimate += 15;
  if (weatherSeverity === "moderate") routeDelayEstimate += 10;

  return {
    weatherSeverity, trafficSeverity, constructionSeverity,
    routeDelayEstimate, weatherNotes, trafficNotes, constructionNotes,
  };
}
